import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Form, InputGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Course } from '../../types';
import { getAllCourses } from '../../services/courseService';
import { FaStar, FaSearch, FaGraduationCap, FaBook, FaVideo, FaUsers, FaChalkboardTeacher } from 'react-icons/fa';

const HomePage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedCourses = await getAllCourses();
      setCourses(fetchedCourses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('حدث خطأ أثناء تحميل الكورسات');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/courses?search=${encodeURIComponent(searchTerm)}`);
  };

  const renderStars = (rating?: number) => {
    const stars = [];
    const ratingValue = rating || 0;
    for (let i = 0; i < 5; i++) {
      stars.push(
        <FaStar
          key={i}
          className={i < ratingValue ? 'text-info' : 'text-muted'}
        />
      );
    }
    return (
      <div className="d-flex">
        {stars}
        <span className="ms-2">{ratingValue}/5</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
        <p className="mt-2">جاري تحميل الكورسات...</p>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <div className="hero-section py-5 bg-primary text-white">
        <Container fluid className="px-0">
          <Row className="align-items-center mx-0">
            <Col lg={6} className="mb-5 mb-lg-0 px-4 px-md-5 animate__animated animate__fadeInUp">
              <h1 className="display-4 fw-bold mb-4">منصة التعليم الإلكتروني الأولى في مصر</h1>
              <p className="lead mb-4 animate__animated animate__fadeInUp animate__delay-1s">
                تعلم مع أفضل المعلمين في مصر واحصل على شهادات معتمدة. دورات في جميع المواد والمراحل التعليمية.
              </p>
              
              <Form onSubmit={handleSearch} className="mb-4 animate__animated animate__fadeInUp animate__delay-2s">
                <InputGroup size="lg">
                  <Form.Control
                    type="search"
                    placeholder="ابحث عن كورس..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="shadow-sm"
                  />
                  <Button variant="info" type="submit" className="px-4 text-white fw-bold">
                    <FaSearch /> بحث
                  </Button>
                </InputGroup>
              </Form>
              
              <div className="d-flex gap-3 animate__animated animate__fadeInUp animate__delay-3s">
                <Button variant="info" size="lg" className="px-4 py-3 shadow text-white fw-bold" onClick={() => navigate('/courses')}>
                  <FaGraduationCap className="me-2" /> استكشف الدورات
                </Button>
                <Button variant="outline-light" size="lg" className="px-4 py-3 shadow-sm fw-bold" onClick={() => navigate('/register')}>
                  <FaUsers className="me-2" /> سجل الآن
                </Button>
              </div>
            </Col>
            <Col lg={6} className="text-center animate__animated animate__fadeInRight animate__delay-2s">
              <img 
                src="/images/hero-image.svg" 
                alt="تعلم عبر الإنترنت" 
                className="img-fluid hero-image"
                style={{ maxHeight: '500px' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = 'https://via.placeholder.com/800x600?text=منصة+التعلم+الإلكتروني';
                }}
              />
            </Col>
          </Row>
        </Container>
      </div>

      {/* Stats Section */}
      <div className="stats-section py-5 bg-light">
        <Container fluid>
          <Row className="text-center justify-content-center">
            <Col md={3} sm={6} className="mb-4 px-0 animate__animated animate__fadeInUp">
              <div className="stat-card p-4 bg-white rounded shadow-sm h-100">
                <div className="stat-icon mb-3">
                  <FaGraduationCap size={40} />
                </div>
                <h3 className="stat-number mb-2 fw-bold">234</h3>
                <p className="stat-title mb-0">دورة تعليمية</p>
              </div>
            </Col>
            <Col md={3} sm={6} className="mb-4 px-0 animate__animated animate__fadeInUp animate__delay-1s">
              <div className="stat-card p-4 bg-white rounded shadow-sm h-100">
                <div className="stat-icon mb-3">
                  <FaUsers size={40} />
                </div>
                <h3 className="stat-number mb-2 fw-bold">6457</h3>
                <p className="stat-title mb-0">طالب مسجل</p>
              </div>
            </Col>
            <Col md={3} sm={6} className="mb-4 px-0 animate__animated animate__fadeInUp animate__delay-2s">
              <div className="stat-card p-4 bg-white rounded shadow-sm h-100">
                <div className="stat-icon mb-3">
                  <FaChalkboardTeacher size={40} />
                </div>
                <h3 className="stat-number mb-2 fw-bold">26</h3>
                <p className="stat-title mb-0">معلم محترف</p>
              </div>
            </Col>
            <Col md={3} sm={6} className="mb-4 px-0 animate__animated animate__fadeInUp animate__delay-3s">
              <div className="stat-card p-4 bg-white rounded shadow-sm h-100">
                <div className="stat-icon mb-3">
                  <FaVideo size={40} />
                </div>
                <h3 className="stat-number mb-2 fw-bold">1250</h3>
                <p className="stat-title mb-0">فيديو تعليمي</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Featured Courses Section */}
      <Container fluid className="py-5 p-0">
        <h2 className="section-title text-center mb-5 animate__animated animate__fadeInUp">الدورات المميزة</h2>
        
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <Row className="course-grid justify-content-center">
            {courses.slice(0, 4).map((course, index) => (
              <Col key={course.id} lg={3} md={6} sm={12} className={`animate__animated animate__fadeInUp animate__delay-${index % 4}s`}>
                <Card className="course-card h-100 shadow-sm border-0">
                  <div className="course-image-container">
                    <Card.Img 
                      variant="top" 
                      src={course.imageUrl || 'https://via.placeholder.com/300x200?text=Course+Image'} 
                      className="course-image"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = 'https://via.placeholder.com/300x200?text=Course+Image';
                      }}
                    />
                    {course.isFeatured && (
                      <Badge bg="info" className="position-absolute top-0 start-0 m-2">مميز</Badge>
                    )}
                    {course.price === 0 && (
                      <Badge bg="success" className="position-absolute top-0 end-0 m-2">مجاني</Badge>
                    )}
                  </div>
                  <Card.Body className="course-content d-flex flex-column">
                    <div className="d-flex align-items-center mb-2">
                      <span className="course-category">{course.category}</span>
                      <small className="text-muted ms-2">{course.level}</small>
                    </div>
                    <Card.Title className="h5 mb-3">{course.title}</Card.Title>
                    <Card.Text className="text-muted mb-3 flex-grow-1">
                      {course.description?.substring(0, 100)}...
                    </Card.Text>
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <div>{renderStars(course.rating)}</div>
                      <div className="course-price">
                        {course.price === 0 ? 'مجاني' : `${course.price} جنيه`}
                      </div>
                    </div>
                    
                    <div className="course-instructor">
                      <img 
                        src={course.teacherImage || 'https://via.placeholder.com/40x40?text=المعلم'} 
                        alt={course.teacherName || 'غير معروف'}
                        className="instructor-avatar"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = 'https://via.placeholder.com/40x40?text=المعلم';
                        }}
                      />
                      <span className="instructor-name">{course.teacherName || 'غير معروف'}</span>
                    </div>
                  </Card.Body>
                  <Card.Footer className="bg-white border-0 pt-0 pb-3 px-3">
                    <Button 
                      variant="outline-primary" 
                      className="w-100"
                      onClick={() => navigate(`/courses/${course.id}`)}
                    >
                      عرض التفاصيل
                    </Button>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        
        <div className="text-center mt-5">
          <Button 
            variant="primary" 
            size="lg" 
            className="px-5 py-3 fw-bold shadow animate__animated animate__pulse animate__infinite animate__slower"
            onClick={() => navigate('/courses')}
          >
            <FaBook className="me-2" />
            عرض جميع الدورات
          </Button>
        </div>
      </Container>

      {/* Features Section */}
      <div className="features-section py-5 bg-light mt-5">
        <Container fluid className="p-0">
          <h2 className="section-title text-center mb-5 animate__animated animate__fadeInUp">لماذا تختار منصتنا؟</h2>
          <Row className="feature-grid justify-content-center">
            <Col lg={4} md={6} className="animate__animated animate__fadeInUp">
              <div className="feature-card bg-white p-4 rounded shadow-sm h-100">
                <div className="feature-icon mb-3 text-primary">
                  <FaGraduationCap size={40} />
                </div>
                <h3 className="feature-title h4 mb-3">محتوى تعليمي متميز</h3>
                <p className="feature-description text-muted">
                  محتوى تعليمي مُعد بعناية من قبل نخبة من المعلمين المتميزين في مختلف المجالات والمراحل التعليمية.
                </p>
              </div>
            </Col>
            <Col lg={4} md={6} className="animate__animated animate__fadeInUp animate__delay-1s">
              <div className="feature-card bg-white p-4 rounded shadow-sm h-100">
                <div className="feature-icon mb-3 text-primary">
                  <FaVideo size={40} />
                </div>
                <h3 className="feature-title h4 mb-3">دروس مرئية عالية الجودة</h3>
                <p className="feature-description text-muted">
                  فيديوهات تعليمية بجودة عالية وشرح مبسط يساعدك على فهم المواد الدراسية بسهولة.
                </p>
              </div>
            </Col>
            <Col lg={4} md={6} className="animate__animated animate__fadeInUp animate__delay-2s">
              <div className="feature-card bg-white p-4 rounded shadow-sm h-100">
                <div className="feature-icon mb-3 text-primary">
                  <FaUsers size={40} />
                </div>
                <h3 className="feature-title h4 mb-3">تواصل مباشر مع المعلمين</h3>
                <p className="feature-description text-muted">
                  إمكانية التواصل المباشر مع المعلمين والاستفسار عن أي جزء من المنهج الدراسي.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Call to Action Section */}
      <div className="cta-section py-5 bg-primary text-white text-center">
        <Container fluid className="p-0">
          <h2 className="mb-4 animate__animated animate__fadeInUp">ابدأ رحلتك التعليمية اليوم</h2>
          <p className="lead mb-5 animate__animated animate__fadeInUp animate__delay-1s">
            انضم إلى آلاف الطلاب الذين يستفيدون من منصتنا التعليمية واحصل على تعليم عالي الجودة
          </p>
          <Button 
            variant="light" 
            size="lg" 
            className="px-5 py-3 fw-bold text-primary shadow animate__animated animate__fadeInUp animate__delay-2s"
            onClick={() => navigate('/register')}
          >
            <FaUsers className="me-2" />
            سجل الآن مجانًا
          </Button>
        </Container>
      </div>
    </div>
  );
};

export default HomePage;
